import { KJUR } from 'jsrsasign';
import { withErrorConsumed } from './common-utils';
import athenaConfig from '../config';
import AmplitudeUtils from './amplitude-utils';
import { getOhswBannerKey, getStateTokenExpiryBannerType, removeStateTokenExpiryBannerType } from './storage-utils';
import { AlertTypes } from '@athena/forge';
import { getLocalizedString } from './i18n-utils';
import { AUTH_SERVER_ID_ANET_SIGNED_JWT_KEY_MAP, INFO_BANNER_MESSAGE_KEY_MAP, INFO_BANNER_MESSAGE_KEY_MAP_V2 } from '../constants';
import { splitAdminContactMessage, prefetchAdminCoverage, getCachedAdminCoverage } from './admin-coverage-utils';
import { getUserStartLoginWorkflowCookie } from './storage-utils';
import React from 'react';
const { oktaUrl, athenaNetPublickKeyUrl, env } = athenaConfig;

interface BannerDisplayConfig {
  description: string | React.ReactNode;
  header: string;
  bannerType: string;
  alertType: AlertTypes;
  key?: string;
}

export interface BannerInfo extends BannerDisplayConfig {
  isSso?: boolean;
  practiceId?: string;
}

export interface AthenaOneParams {
  ARGS: string[] | null;
  BANNERTYPE: string;
  PRACTICEID?: string;
  STACKNUMBER?: string;
  KEY: string;
  LASTLOGINET?: number;
  SSOREAUTHPARAMS?: Record<string, string>;
}

interface DecodedJWT {
  exp: number;
  aud: string;
  data: AthenaOneParams;
  iat: number;
}


const aNetPublicKeyCache: any = {};

export const _getAnetTokenPublicKey = async () => {
  let resolvedEnv = env;
  if (env === 'prod') {
    const authorizationServerId = OktaUtil.getRequestContext()?.authentication?.issuer?.id;
    if (authorizationServerId && AUTH_SERVER_ID_ANET_SIGNED_JWT_KEY_MAP[authorizationServerId]) {
      resolvedEnv = AUTH_SERVER_ID_ANET_SIGNED_JWT_KEY_MAP[authorizationServerId];
    }
  }
  if (!aNetPublicKeyCache[resolvedEnv]) {
    try {
      const response = await fetch(athenaNetPublickKeyUrl);
      const data = await response.json();
      aNetPublicKeyCache[resolvedEnv] = data[resolvedEnv].publicKeys[0];
    }
    catch (error: any) {
      AmplitudeUtils.logCustomEvent('FetchAnetPublicKeysFailure', {
        errorMessage: error?.message,
        errorCode: error?.code,
      });
      return null;
    }
  }
  return aNetPublicKeyCache[resolvedEnv];
};

export const _parseAndValidateJwt = withErrorConsumed(async (token: string): Promise<AthenaOneParams | null> => {
  const publicKey = await _getAnetTokenPublicKey();
  if (!publicKey) {
    return null;
  }
  const decodedToken = KJUR.jws.JWS.parse(token);
  const claims = decodedToken.payloadObj as DecodedJWT;
  const isValid = KJUR.jws.JWS.verifyJWT(token, publicKey, { alg: [ 'RS256' ], verifyAt: claims.iat });
  if (!isValid) {
    AmplitudeUtils.logCustomEvent('InvalidBannerJwt', { errorMessage: 'Jwt Verification Failed' });
    return null;
  }
  if (claims.aud !== oktaUrl) {
    AmplitudeUtils.logCustomEvent('InvalidBannerJwt', { errorMessage: 'Invalid Aud', aud: claims.aud });
    return null;
  }
  return claims.data as AthenaOneParams;
});

export const getAthenaOneParamsJWTFromUrl = withErrorConsumed((): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('BANNERARGS');
});

export const getStateTokenExpiryBannerConfig = (bannerType: string): BannerDisplayConfig | null  => {
  const messageConfig = INFO_BANNER_MESSAGE_KEY_MAP_V2[bannerType]?.['DEFAULT'];
  if (messageConfig?.header) {
    return {
      key: 'DEFAULT',
      description: getLocalizedString(messageConfig?.description),
      header: getLocalizedString(messageConfig?.header),
      bannerType,
      alertType: 'info' as AlertTypes,
    };
  }
  return null;
};

const _getV2BannerConfig = async (
  athenaOneParams: AthenaOneParams,
  athenaOneWebSso?: boolean
) => {
  const { BANNERTYPE, KEY, ARGS } = athenaOneParams;
  const messageConfig = INFO_BANNER_MESSAGE_KEY_MAP_V2[BANNERTYPE]?.[KEY];
  let bannerDescription: string | React.ReactNode = getLocalizedString(messageConfig?.description);
  const bannerHeader = getLocalizedString(messageConfig?.header);

  const ARGSarr = typeof ARGS === 'string' ? [ ARGS ] : ARGS;
  // Dynamically replace placeholders and format with admin coverage (only if it's a string)
  if (typeof bannerDescription === 'string') {
    ARGSarr?.forEach((arg: string, index: number) => {
      bannerDescription = (bannerDescription as string).replace(new RegExp(`\\{${index}\\}`, 'g'), arg);
    });

    const { prefix, hasAdminPhrase } = splitAdminContactMessage(bannerDescription);
    // Temporary fix for UIAMUA-7967
    if (hasAdminPhrase && KEY !== 'UNKNOWNANETUSER') {
      // Get username from JWT params (backend includes it) or cookie (user entered it)
      const username = getUserStartLoginWorkflowCookie();
      if (username) {
        // Fetch admin coverage data for this user
        await prefetchAdminCoverage(username);
        const coverage = getCachedAdminCoverage();

        if (coverage) {
          if (!coverage.hasAdmin) {
            // No admin - use CSC contact
            const cscPhrase = getLocalizedString('admin.coverage.no.admin');
            const capitalizedPhrase = cscPhrase.charAt(0).toUpperCase() + cscPhrase.slice(1);
            bannerDescription = `${prefix} ${capitalizedPhrase}`;
          }
          else if (coverage.contextSupportMessages.length > 0) {
            // Has admin with support messages - format as JSX with line breaks
            const supportInstructions = getLocalizedString('admin.coverage.support.instructions.prefix');
            bannerDescription = (
              <>
                {prefix} {supportInstructions}
                <br />
                {coverage.contextSupportMessages.map(
                  (msg: { contextId: string; supportMessage: string }, idx: number) => (
                    <span key={idx}>
                      {msg.contextId} - {msg.supportMessage}
                      {idx < coverage.contextSupportMessages.length - 1 && <br />}
                    </span>
                  ))}
              </>
            );
          }
          // Else: has admin but no support messages - keep original error message unchanged
        }
      }
    }
  }

  return {
    description: bannerDescription,
    header: bannerHeader,
    bannerType: BANNERTYPE,
    alertType: messageConfig?.alertType,
    isSso: !!(athenaOneWebSso || new URLSearchParams(window.location.search).get('idp')),
    key: KEY,
    practiceId: (athenaOneParams.PRACTICEID || athenaOneParams.SSOREAUTHPARAMS?.PRACTICEID),
  };
};

const _getV1BannerConfig = () => {
  const ohswBannerKey = getOhswBannerKey();
  if (ohswBannerKey) {
    const messageConfig = INFO_BANNER_MESSAGE_KEY_MAP[ohswBannerKey];
    if (messageConfig?.header) {
      return {
        description: getLocalizedString(messageConfig.description),
        header: getLocalizedString(messageConfig.header),
        bannerType: ohswBannerKey,
        alertType: 'info' as AlertTypes,
        isSso: !!(new URLSearchParams(window.location.search).get('idp')),
      };
    }
  }
  return null;
};

// eslint-disable-next-line max-len
export const getBannerConfig = async (athenaOneParams?: AthenaOneParams, athenaOneWebSso?: boolean) : Promise<BannerInfo | null> => {
  let bannerConfig: BannerInfo | null = null;
  if (athenaOneParams) {
    const { BANNERTYPE, KEY } = athenaOneParams;
    if (INFO_BANNER_MESSAGE_KEY_MAP_V2[BANNERTYPE]?.[KEY]) {
      bannerConfig = await _getV2BannerConfig(athenaOneParams, athenaOneWebSso);
    }
  }
  else {
    bannerConfig = _getV1BannerConfig();
  }
  const stateTokenExpiryBannerType = getStateTokenExpiryBannerType();
  if (stateTokenExpiryBannerType) {
    removeStateTokenExpiryBannerType();
    const stateTokenExpiryBannerConfig =  getStateTokenExpiryBannerConfig(stateTokenExpiryBannerType);
    if (stateTokenExpiryBannerConfig) {
      bannerConfig = {
        ...bannerConfig,
        ...stateTokenExpiryBannerConfig,
      };
    }
  }
  return bannerConfig;
};


export const getAthenaOneParamsFromJwt = async (): Promise<AthenaOneParams | undefined> => {
  const athenaOneParamsJwt = getAthenaOneParamsJWTFromUrl();
  if (athenaOneParamsJwt) {
    const athenaOneParams = await _parseAndValidateJwt(athenaOneParamsJwt);
    if (athenaOneParams) {
      return athenaOneParams;
    }
  }
};

export const getBannerTypeForSessionExpiry = async (): Promise<string> => {
  const athenaOneParams = await getAthenaOneParamsFromJwt();
  if (athenaOneParams) {
    const bannerType = athenaOneParams.BANNERTYPE;
    if (bannerType && [ 'TIMEDOUT', 'SESSIONLOCKED', 'CONCIERGEMODE' ].includes(bannerType)) {
      return 'TIMEDOUT';
    }
  }
  return 'REFRESHTIMEOUT';
};
